import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './services/permission.guard';

// ── Eagerly-loaded components (entry points & lightweight pages) ──
import { HomeComponent } from './views/home/home.component';
import { LoginComponent } from './views/login/login.component';
import { CategoryComponent } from './views/category/category.component';
import { SignupComponent } from './views/signup/signup.component';
import { ChangePasswordComponent } from './views/change-password/change-password.component';
import { ActivateAccountComponent } from './views/login/activate-account/activate-account.component';
import { UserProfileComponent } from './views/user-profile/user-profile.component';
import { SearchResultComponent } from './views/category/search-result/search-result.component';
import { SearchViewComponent } from './views/category/search-view/search-view.component';
import { AdvanceSearchComponent } from './views/category/advance-search/advance-search.component';
import { ListServicesComponent } from './views/category/list-services/list-services.component';
import { SingleDataViewComponent } from './views/single-data-view/single-data-view.component';
import { AllDataViewComponent } from './views/all-data-view/all-data-view.component';
import { NotificationComponent } from './notification/notification.component';
import { AssignUsersComponent } from './assign-users/assign-users.component';
import { InvestorControllerComponent } from './investor-controller/investor-controller.component';
import { TrainingRevampComponent } from './views/home/training-revamp/training-revamp.component';
import { SurveyRedirectGuard } from './guards/survey-redirect.guard';
import { TrainingPortalRedirectGuard } from './guards/training-portal-redirect.guard';
import { RedirectSurveyComponent } from './misc/redirect-survey-component/redirect-survey-component.component';
import { PreodicReportsComponent } from './preodic-reports/preodic-reports.component';
import { ContentExpertsManagementComponent } from './preodic-reports/content-experts-management/content-experts-management.component';
import { ThemeSettingsComponent } from './theme-settings/theme-settings.component';
// permission request components — routes disabled for now, to be managed later
// import { PermissionRequestsComponent } from './permission-requests/permission-requests.component';
// import { PermissionRequestFlowComponent } from './permission-requests/permission-request-flow/permission-request-flow.component';
import { BulkComponent } from './admin/views/data/bulk/bulk.component';
import { ContentUpdatePageComponent } from './admin/views/data/content-update-page/content-update-page.component';
import { SurveyViewComponent } from './admin/views/survey/survey-view/survey-view.component';
import { VideosComponent } from './views/static-pages/videos/videos.component';
import { UserGuideZatcaComponent } from './views/static-pages/user-guides/user-guide-zatca.component';

// ── Eagerly-loaded components for :from/* routes ──
import { AssessmentResultsComponent } from './admin/views/assessment/assessment-results/assessment-results.component';
import { SurveyTagsManagementComponent } from './admin/views/survey/survey-tags-management/survey-tags-management.component';
import { AddQuestionComponent } from './admin/views/survey/add-question/add-question.component';
import { PdfBuilderComponent } from './pdf-builder/pdf-builder.component';

// ── Eagerly-loaded Knowledge Library (mixed route prefixes) ──
import { ViewComponent } from './admin/views/knowledge_library/view/view.component';
import { KLibraryAdminComponent } from './admin/views/knowledge_library/admin/klibrary_admin.component';
import { KCategoryComponent } from './admin/views/knowledge_library/k-category/k-category.component';
import { KlApprovalComponent } from './admin/views/training/kl-approval/kl-approval.component';

// ── Eagerly-loaded Company (scattered route prefixes) ──
import { CompanyComponent } from './admin/views/company/company.component';
import { AddExternalCoordinatorComponent } from './admin/views/company/add-external-coordinator/add-external-coordinator.component';
import { CoordinatorViewComponent } from './admin/views/company/coordinator-view/coordinator-view.component';
import { ViewTrainersComponent } from './admin/views/company/view-trainers/view-trainers.component';

// ── Eagerly-loaded Products (scattered route prefixes) ──
import { ProductsServicesComponent } from './products-services/products-services.component';
import { ProductServicesFormComponent } from './products-services/product-services-form/product-services-form.component';
import { ProductOrdersComponent } from './products-services/product-orders/product-orders.component';
import { OrderDetailsComponent } from './products-services/order-details/order-details.component';
import { OrderListingComponent } from './products-services/order-listing/order-listing.component';
import { OrderDetailComponent } from './products-services/order-detail/order-detail.component';
import { ProductTypeComponent } from './products-services/product-type/product-type.component';

const routes: Routes = [
  // ══════════════════════════════════════════════════════════════
  // Open / Public Routes (eagerly loaded)
  // ══════════════════════════════════════════════════════════════
  { path: 'home', component: HomeComponent, canActivate: [AuthGuard] },
  { path: 'category/:id', component: CategoryComponent },
  { path: 'category/search/:searchquery', component: SearchResultComponent },
  { path: 'category/search/:categoryid/:searchquery', component: SearchResultComponent },
  { path: 'list-services/:id/:type', component: ListServicesComponent },
  { path: 'data/:id', component: SingleDataViewComponent },
  { path: 'login', component: LoginComponent },
  { path: 'signup', component: SignupComponent },
  { path: 'account/activate', component: ActivateAccountComponent },
  { path: 'user-profile', component: UserProfileComponent },
  { path: 'password-change', component: ChangePasswordComponent },
  { path: 'new-ui', component: TrainingRevampComponent },
  { path: 'user-guide-zatca', component: UserGuideZatcaComponent },
  { path: 'video-content', component: VideosComponent },

  // ══════════════════════════════════════════════════════════════
  // Search & Data (eagerly loaded — lightweight)
  // ══════════════════════════════════════════════════════════════
  { path: 'search-view', component: SearchViewComponent, canActivate: [AuthGuard] },
  { path: 'advance-search', component: AdvanceSearchComponent, canActivate: [AuthGuard] },
  { path: 'advance-search/result', component: SearchViewComponent, canActivate: [AuthGuard] },
  { path: 'all-data-view', component: AllDataViewComponent, canActivate: [AuthGuard] },
  { path: 'notification', component: NotificationComponent, canActivate: [AuthGuard] },

  // Routes that don't match a lazy-loaded prefix but use admin data components
  { path: 'bulk', component: BulkComponent, canActivate: [AuthGuard] },
  { path: 'update', component: ContentUpdatePageComponent, canActivate: [AuthGuard] },

  // ══════════════════════════════════════════════════════════════
  // LAZY-LOADED FEATURE MODULES
  // ══════════════════════════════════════════════════════════════

  // Admin module — organization units, users/roles, data, comments, dashboards
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin-feature.module').then(m => m.AdminFeatureModule),
  },

  // Browse trainings now lives in the Tayseer portal (served at /training).
  // Must stay ABOVE the lazy 'training' route so the feature module is never
  // loaded just to bounce the user out of it.
  {
    path: 'training/all-trainings',
    canActivate: [TrainingPortalRedirectGuard],
    children: [],
  },

  // Training module — all training, courses, training pages.
  //
  // Its routes are all commented out: training lives in the Tayseer portal now
  // (see the note in training-feature.module.ts). The module stays lazy-loaded
  // so its components, which other modules declare against, still build; with
  // no routes left, anything under /training that the redirect above does not
  // claim falls through to the wildcard and lands on home.
  {
    path: 'training',
    loadChildren: () => import('./training/training-feature.module').then(m => m.TrainingFeatureModule),
  },

  // Survey module
  {
    path: 'survey',
    loadChildren: () => import('./survey/survey-feature.module').then(m => m.SurveyFeatureModule),
  },

  // Assessment module
  {
    path: 'assessment',
    loadChildren: () => import('./assessment/assessment-feature.module').then(m => m.AssessmentFeatureModule),
  },

  // Mystery Shopper module
  {
    path: 'ms',
    loadChildren: () => import('./mystery-shopper/mystery-shopper-feature.module').then(m => m.MysteryShopperFeatureModule),
  },
  // legacy mystery-shopper route → redirects into ms module
  { path: 'mystery-shopper', redirectTo: 'ms', pathMatch: 'full' },

  // Knowledge Library module
  // NOTE: Kept eagerly loaded due to mixed route prefixes (knowledge-library/ and dashboard/knowledge-library/)

  // PDF Maker module
  {
    path: 'pdf-maker',
    loadChildren: () => import('./pdf-maker/pdf-feature.module').then(m => m.PdfFeatureModule),
  },

  // Announcement module
  {
    path: 'announcement',
    loadChildren: () => import('./announcement/announcement-feature.module').then(m => m.AnnouncementFeatureModule),
  },

  // ══════════════════════════════════════════════════════════════
  // Eagerly-loaded: Knowledge Library (mixed route prefixes)
  // ══════════════════════════════════════════════════════════════
  { path: 'knowledge-library', component: ViewComponent, canActivate: [AuthGuard] },
  { path: 'knowledge-library/approval', component: KlApprovalComponent, canActivate: [AuthGuard] },
  { path: 'dashboard/knowledge-library', component: KLibraryAdminComponent, canActivate: [AuthGuard] },
  { path: 'dashboard/knowledge-library/category', component: KCategoryComponent, canActivate: [AuthGuard] },

  // ══════════════════════════════════════════════════════════════
  // Eagerly-loaded: Company (scattered route prefixes)
  // ══════════════════════════════════════════════════════════════
  { path: 'company', component: CompanyComponent },
  { path: 'trainers', component: ViewTrainersComponent },
  { path: 'add-external-coordinator', component: AddExternalCoordinatorComponent },
  { path: 'coordinator-view', component: CoordinatorViewComponent, canActivate: [AuthGuard] },

  // ══════════════════════════════════════════════════════════════
  // Eagerly-loaded: Products (scattered route prefixes)
  // ══════════════════════════════════════════════════════════════
  { path: 'products-view', component: ProductsServicesComponent, canActivate: [AuthGuard] },
  { path: 'products-view/product-type', component: ProductTypeComponent, canActivate: [AuthGuard] },
  { path: 'product-services-form', component: ProductServicesFormComponent, canActivate: [AuthGuard] },
  { path: 'product-orders', component: ProductOrdersComponent, canActivate: [AuthGuard] },
  { path: 'order-details/:uid', component: OrderDetailsComponent, canActivate: [AuthGuard] },
  { path: 'orders', component: OrderListingComponent, canActivate: [AuthGuard] },
  { path: 'orders/:id', component: OrderDetailComponent, canActivate: [AuthGuard] },

  // ══════════════════════════════════════════════════════════════
  // Remaining eagerly-loaded protected routes
  // ══════════════════════════════════════════════════════════════
  // permission request routes hidden for now, to be managed later — also disabled for direct URL access (falls back to '**' → home)
  // { path: 'permission-request-flow', component: PermissionRequestFlowComponent, canActivate: [AuthGuard] },
  // { path: 'permission/requests', component: PermissionRequestsComponent },
  { path: 'periodic-reports', component: PreodicReportsComponent, canActivate: [AuthGuard] },
  { path: 'content-experts', component: ContentExpertsManagementComponent, canActivate: [AuthGuard] },
  { path: 'theme-settings', component: ThemeSettingsComponent, canActivate: [AuthGuard] },
  { path: 'investor-admin', component: InvestorControllerComponent, canActivate: [AuthGuard] },

  // Branch trainers & update-dates (redirect to training/ lazy module)
  { path: 'branch-trainers', redirectTo: 'training/branch-trainers', pathMatch: 'full' },
  { path: 'update-dates', redirectTo: 'training/update-dates', pathMatch: 'full' },

  // Survey result redirect
  {
    path: 'user-survey-result/:surveyId/:email',
    canActivate: [SurveyRedirectGuard],
    component: RedirectSurveyComponent,
  },

  // ══════════════════════════════════════════════════════════════
  // Dynamic :from routes (MUST come AFTER all specific routes)
  // ══════════════════════════════════════════════════════════════
  { path: ':from/results/:assessmentId', component: AssessmentResultsComponent, canActivate: [AuthGuard] },
  { path: ':from/tags-management', component: SurveyTagsManagementComponent },
  { path: ':from/attempt/:surveyId', component: SurveyViewComponent },
  { path: ':from/add-question/:questionId', component: AddQuestionComponent },
  { path: ':from/pdf-builder', component: PdfBuilderComponent },

  // Wildcard fallback
  { path: '**', redirectTo: 'home' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
